/* SchemaForm — renders a CMS module from a schema spec.
 * Each section is a collapsible card; each field is rendered via FieldRender.
 * After the explicit sections, any OTHER top-level keys present on the doc are
 * rendered via DynamicSection so the CMS surfaces 100% of existing content. */
import { useState } from "react";
import FieldRender from "./_FieldRender";
import { isValidEmail, isValidUrl, isValidSlug } from "./_fields";
import DynamicSection, { dynamicKeys } from "./_DynamicSection";

/* --- nested key helpers ("hero.primary_cta.label") --- */
function getDeep(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function setDeep(obj, path, value) {
  const keys = path.split(".");
  const next = { ...(obj || {}) };
  let cur = next;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const k = keys[i];
    cur[k] = {
      ...(cur[k] && typeof cur[k] === "object" && !Array.isArray(cur[k]) ? cur[k] : {}),
    };
    cur = cur[k];
  }
  if (value === "" || value == null) {
    delete cur[keys[keys.length - 1]];
  } else {
    cur[keys[keys.length - 1]] = value;
  }
  return next;
}

function validate(schema, doc) {
  const errs = {};
  for (const sec of schema.sections) {
    for (const f of sec.fields) {
      const v = getDeep(doc, f.key);
      if (f.required && (v === undefined || v === null || v === "")) {
        errs[f.key] = `${f.label} is required`;
      } else if (v) {
        if (f.type === "email" && !isValidEmail(v)) errs[f.key] = "Invalid email";
        if (f.type === "url" && !isValidUrl(v)) errs[f.key] = "Invalid URL";
        if (f.type === "slug" && !isValidSlug(v))
          errs[f.key] = "Slugs must be lowercase letters, numbers and hyphens only";
      }
    }
  }
  return errs;
}

function Section({ section, value, onChange, errors }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="bg-white border border-black/10 rounded-sm">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-3 flex items-center justify-between bg-[#F8F6F1] border-b border-black/10"
      >
        <div className="font-serif text-base">{section.title}</div>
        <div className="text-xs text-[#4B5563]">{open ? "Hide" : "Show"}</div>
      </button>
      {open && (
        <div className="p-5 space-y-5">
          {section.fields.map((f) => (
            <div key={f.key}>
              <FieldRender
                field={f}
                value={getDeep(value, f.key)}
                onChange={(v) => onChange(setDeep(value, f.key, v))}
                errors={errors}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function SchemaForm({ schema, value, onChange, errors }) {
  // Collect all keys explicitly handled by the schema so we know what to
  // surface as "extra" dynamic sections.
  const handledRoots = new Set();
  schema.sections.forEach((sec) =>
    sec.fields.forEach((f) => handledRoots.add(String(f.key).split(".")[0]))
  );
  const extras = dynamicKeys(value, handledRoots);

  return (
    <div className="space-y-6">
      {schema.sections.map((sec, idx) => (
        <Section
          key={`${schema.collection}-${idx}`}
          section={sec}
          value={value}
          onChange={onChange}
          errors={errors}
        />
      ))}
      {extras.length > 0 && (
        <ExtrasSection extras={extras} value={value} onChange={onChange} />
      )}
    </div>
  );
}

function ExtrasSection({ extras, value, onChange }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="bg-white border border-black/10 rounded-sm">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-3 flex items-center justify-between bg-[#FBF3D5]/40 border-b border-black/10"
      >
        <div className="font-serif text-base">
          More content fields
          <span className="ml-2 text-xs text-[#7A5A06]">({extras.length})</span>
        </div>
        <div className="text-xs text-[#4B5563]">{open ? "Hide" : "Show"}</div>
      </button>
      {open && (
        <div className="p-5 space-y-4">
          <p className="text-xs text-[#4B5563]">
            These fields exist on this entry but aren&apos;t in the standard form
            above. They&apos;re editable here.
          </p>
          {extras.map((k) => (
            <DynamicSection
              key={k}
              k={k}
              value={value?.[k]}
              onChange={(nv) => onChange({ ...value, [k]: nv })}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export { validate, getDeep, setDeep };
